'use client';

import { useLatestNews } from '@outpoll/strapi-client';
import { useTranslations } from 'next-intl';
import { type ComponentType, useMemo } from 'react';
import { useSearchPairs } from '@/lib/api/futures/hooks';
import { useRewardsLeaderboard } from '@/lib/api/rewards-leaderboard/hooks';
import { useTrendingPredictions } from '@/lib/api/search/hooks';
import { LEADERBOARD_PATH } from '@/lib/components/RewardsHub/routes';
import { TRADE_PAIR_LIST_HREF } from '@/lib/constants/routing';
import { pickTopPairs } from '@/lib/utils/pairFormat';
import { PREVIEW_LIMIT, SECTION_LIMIT } from './core/constants';
import type { SearchCategory, SearchTab } from './core/types';
import { NewsRow } from './rows/NewsRow';
import { PairRow } from './rows/PairRow';
import { PredictionRow } from './rows/PredictionRow';
import { TraderRow, type TraderRowData } from './rows/TraderRow';
import { EmptyPreview } from './shared/EmptyPreview';
import { RecentSearches } from './shared/RecentSearches';
import { SkeletonRows } from './shared/SearchSkeleton';
import { Section, SectionAction } from './shared/Section';
import { SectionPresence } from './shared/SectionPresence';

/**
 * The terminal with its pair list open, the markets, the leaderboard and the
 * news feed — each section's "See all".
 */
const MARKETS_HREF = '/';
const LEADERBOARD_HREF = LEADERBOARD_PATH;
const NEWS_HREF = '/news';

interface PreviewProps {
  /** Three under All, more on the category's own tab. */
  limit: number;
}

/**
 * The pairs on top of the list: 🔥 trending first. The service keeps no count
 * of what is searched, so "Top searched pairs" is the admin's trending pick.
 */
function TopPairs({ limit }: PreviewProps) {
  const t = useTranslations('search');
  const { data: pairs, isLoading } = useSearchPairs();
  const top = useMemo(() => pickTopPairs(pairs ?? [], limit), [pairs, limit]);

  return (
    <SectionPresence show={isLoading || top.length > 0}>
      <Section
        title={t('topPairs')}
        action={<SectionAction label={t('seeAll')} href={TRADE_PAIR_LIST_HREF} />}
      >
        {isLoading ? (
          <SkeletonRows thumb="coin" count={limit} />
        ) : (
          top.map((pair) => <PairRow key={pair.id} pair={pair} />)
        )}
      </Section>
    </SectionPresence>
  );
}

function TrendingPredictions({ limit }: PreviewProps) {
  const t = useTranslations('search');
  const { data, isPending } = useTrendingPredictions(limit);

  return (
    <SectionPresence show={isPending || Boolean(data?.length)}>
      <Section
        title={t('trendingPredictions')}
        action={<SectionAction label={t('seeAll')} href={MARKETS_HREF} />}
      >
        {isPending ? (
          <SkeletonRows thumb="square" count={limit} />
        ) : (
          data?.map((prediction) => <PredictionRow key={prediction.id} prediction={prediction} />)
        )}
      </Section>
    </SectionPresence>
  );
}

/**
 * The biggest traders by volume, over all time and both products — the
 * Rewards Hub's board. The board is public and the section stays without the
 * hub; only the rank and tasks line (hub progress) goes with it (user, 15.10).
 */
function TopTraders({ limit }: PreviewProps) {
  const t = useTranslations('search');
  const { data, isPending } = useRewardsLeaderboard({
    segment: 'all',
    metric: 'volume',
    period: 'all',
    page: 1,
  });
  const traders = useMemo<TraderRowData[]>(
    () =>
      (data?.entries ?? []).slice(0, limit).map((entry) => ({
        userId: entry.userId,
        username: entry.username,
        look: entry.look,
        position: entry.position,
        rankIndex: entry.rankIndex,
        tasks: entry.tasks,
        volume: entry.volume,
        activity: t('trades', { count: entry.trades }),
      })),
    [data, limit, t],
  );

  return (
    <SectionPresence show={isPending || traders.length > 0}>
      <Section
        title={limit > SECTION_LIMIT ? t('topTradersAll') : t('topTraders')}
        action={<SectionAction label={t('leaderboard')} href={LEADERBOARD_HREF} />}
      >
        {isPending ? (
          <SkeletonRows thumb="coin" count={limit} />
        ) : (
          traders.map((trader) => <TraderRow key={trader.userId} trader={trader} />)
        )}
      </Section>
    </SectionPresence>
  );
}

function LatestNews({ limit }: PreviewProps) {
  const t = useTranslations('search');
  const { data, isPending } = useLatestNews(limit);

  return (
    <SectionPresence show={isPending || Boolean(data?.items.length)}>
      <Section
        title={t('latestNews')}
        action={<SectionAction label={t('seeAll')} href={NEWS_HREF} />}
      >
        {isPending ? (
          <SkeletonRows thumb="wide" count={limit} />
        ) : (
          data?.items.map((item) => <NewsRow key={item.id} item={item} />)
        )}
      </Section>
    </SectionPresence>
  );
}

/** Each category's section, as All and its own tab show it before a query. */
const PREVIEWS: Record<SearchCategory, ComponentType<PreviewProps>> = {
  crypto: TopPairs,
  predictions: TrendingPredictions,
  traders: TopTraders,
  news: LatestNews,
};

interface DefaultViewProps {
  tab: SearchTab;
}

/**
 * The search before anything is typed (Figma "1.2 Default — recent
 * searches"): what the player searched lately, then a little of everything —
 * or, on a category's tab, more of that category. Each section loads and
 * fails on its own; one that has nothing drops out.
 */
export function DefaultView({ tab }: DefaultViewProps) {
  if (tab !== 'all') {
    const Preview = PREVIEWS[tab];
    return (
      <>
        <Preview limit={PREVIEW_LIMIT} />
        <EmptyPreview category={tab} />
      </>
    );
  }
  return (
    <>
      <RecentSearches />
      <TopPairs limit={SECTION_LIMIT} />
      <TrendingPredictions limit={SECTION_LIMIT} />
      <TopTraders limit={SECTION_LIMIT} />
      <LatestNews limit={SECTION_LIMIT} />
      <EmptyPreview />
    </>
  );
}
